import React, { useState, useEffect, useMemo } from "react";
import graphQLSegmentsApiService from "../services/GraphQLSegmentsApiService";

const SET_CONFIG = {
  HIGHEST_SCALE: {
    label: "Highest Scale",
    color: "#0076bd",
    lightColor: "#e8f4fb",
    desc: "Relevance × reach",
  },
  BEST_PERFORMANCE: {
    label: "Best Performance",
    color: "#00875a",
    lightColor: "#e3f5ef",
    desc: "Relevance × impressions",
  },
  MOST_RELEVANT: {
    label: "Most Relevant",
    color: "#6b4fbb",
    lightColor: "#f0ecfa",
    desc: "Relevance score only",
  },
};

const SET_ORDER = ["HIGHEST_SCALE", "BEST_PERFORMANCE", "MOST_RELEVANT"];

function fmtBig(v) {
  if (v == null) return "—";
  const n = Number(v);
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toLocaleString();
}

function fmtPct(v) {
  if (v == null) return "—";
  const n = Number(v);
  return n < 1 ? `${(n * 100).toFixed(2)}%` : `${n.toFixed(2)}%`;
}

function fmtCpm(v) {
  if (v == null) return "—";
  return `$${Number(v).toFixed(2)}`;
}

function MetricRow({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "4px 0",
        borderBottom: "1px solid #f0f0f0",
        fontSize: "0.8rem",
      }}
    >
      <span style={{ color: "#666" }}>{label}</span>
      <span style={{ fontWeight: 600, color: "#333" }}>{value}</span>
    </div>
  );
}

function groupByKeyword(segmentScores, segmentMap) {
  const map = {};
  (segmentScores || []).forEach(({ segmentId, score, matchedKeyword }) => {
    const kw = matchedKeyword || "Other";
    if (!map[kw]) map[kw] = [];
    map[kw].push({ segmentId, score, matchedKeyword });
  });

  return Object.entries(map)
    .map(([keyword, scores]) => {
      const sorted = [...scores].sort((a, b) => b.score - a.score);
      let totalReach = 0, totalUniqueUsers = 0, totalImpressions = 0;
      sorted.forEach(({ segmentId }) => {
        const seg = segmentMap[segmentId];
        if (!seg) return;
        const sm = seg.segmentMetrics || {};
        totalReach += sm.reach || 0;
        totalUniqueUsers += sm.uniqueUsers || 0;
        totalImpressions += sm.weightedImpressions || 0;
      });
      return { keyword, scores: sorted, aggregatedMetrics: { totalReach, totalUniqueUsers, totalImpressions } };
    })
    .sort((a, b) => (b.scores[0]?.score || 0) - (a.scores[0]?.score || 0));
}

function SegmentRow({ scored, segment, index, color }) {
  const sv = segment?.segmentVector;
  const sm = segment?.segmentMetrics;
  return (
    <div
      style={{
        padding: "8px 10px",
        borderRadius: "6px",
        background: index % 2 === 0 ? "#fafafa" : "#fff",
        marginBottom: "4px",
        border: "1px solid #eee",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{ fontWeight: 600, fontSize: "0.82rem", color: "#333", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            title={sv?.segmentName || scored.segmentId}
          >
            {sv?.segmentName || scored.segmentId}
          </div>
          <div style={{ fontSize: "0.72rem", color: "#888", marginTop: "2px" }}>
            {[sv?.audienceType, sv?.categoryL1, sv?.source].filter(Boolean).join(" · ")}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: "0.82rem", fontWeight: 700, color: color }}>
            {Number(scored.score).toFixed(3)}
          </div>
          <div style={{ fontSize: "0.7rem", color: "#999" }}>score</div>
        </div>
      </div>
      {sm && (
        <div style={{ display: "flex", gap: "12px", marginTop: "6px", fontSize: "0.72rem", color: "#777" }}>
          {sm.reach != null && <span>Reach: {fmtBig(sm.reach)}</span>}
          {sm.weightedBidCpm != null && <span>Bid CPM: {fmtCpm(sm.weightedBidCpm / 1e6)}</span>}
          {sm.weightedImpressions != null && <span>Imp: {fmtBig(sm.weightedImpressions)}</span>}
          {sm.weightedClickThroughRate != null && <span>CTR: {fmtPct(sm.weightedClickThroughRate)}</span>}
        </div>
      )}
    </div>
  );
}

function KeywordCategory({ keyword, scores, segmentMap, color, defaultExpanded, hllData }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const hllLoading = !hllData || hllData.loading;
  const hllM = hllData?.metrics;

  return (
    <div style={{ marginBottom: "10px", border: `1px solid ${color}33`, borderRadius: "8px", overflow: "hidden" }}>
      <button
        onClick={() => setExpanded((e) => !e)}
        style={{
          width: "100%",
          background: `${color}0f`,
          border: "none",
          borderBottom: expanded ? `1px solid ${color}22` : "none",
          cursor: "pointer",
          padding: "7px 12px",
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          textAlign: "left",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
          <span style={{ fontWeight: 700, fontSize: "0.8rem", color, flex: 1 }}>{keyword}</span>
          <span style={{ background: color, color: "#fff", borderRadius: "10px", padding: "1px 8px", fontSize: "0.68rem", flexShrink: 0 }}>
            {scores.length} segments
          </span>
          {hllLoading && (
            <span style={{ width: "10px", height: "10px", border: `2px solid ${color}44`, borderTopColor: color, borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
          )}
          <span style={{ fontSize: "0.7rem", color: "#999", flexShrink: 0 }}>{expanded ? "▲" : "▼"}</span>
        </div>
        {!hllLoading && hllM && (
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            {hllM.reach != null && <span style={{ fontSize: "0.7rem", color: "#555" }}>Reach: {fmtBig(hllM.reach)}</span>}
            {hllM.totalImpressions != null && <span style={{ fontSize: "0.7rem", color: "#555" }}>Imp: {fmtBig(hllM.totalImpressions)}</span>}
            {hllM.avgCpm != null && <span style={{ fontSize: "0.7rem", color: "#555" }}>CPM: {fmtCpm(hllM.avgCpm)}</span>}
            {hllM.viewability != null && <span style={{ fontSize: "0.7rem", color: "#555" }}>VB: {fmtPct(hllM.viewability)}</span>}
            {hllM.bidGuidance != null && <span style={{ fontSize: "0.7rem", color: "#555" }}>Bid: {fmtCpm(hllM.bidGuidance)}</span>}
            {hllM.reachMethod && <span style={{ fontSize: "0.68rem", color: "#aaa" }}>{hllM.reachMethod}</span>}
          </div>
        )}
      </button>
      {expanded && (
        <div style={{ padding: "8px 10px" }}>
          {scores.map((scored, i) => (
            <SegmentRow
              key={scored.segmentId}
              scored={scored}
              segment={segmentMap[scored.segmentId]}
              index={i}
              color={color}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SetCard({ set, segmentMap, hllMetrics = {} }) {
  const [expanded, setExpanded] = useState(true);
  const cfg = SET_CONFIG[set.type] || { label: set.type, color: "#555", lightColor: "#f5f5f5", desc: "" };
  const m = set.metrics || {};

  const keywordGroups = useMemo(
    () => groupByKeyword(set.segmentScores, segmentMap),
    [set.segmentScores, segmentMap]
  );

  return (
    <div
      style={{
        flex: "1 1 300px",
        minWidth: 0,
        background: "#fff",
        borderRadius: "10px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ background: cfg.color, padding: "14px 16px", color: "#fff" }}>
        <div style={{ fontSize: "1rem", fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{cfg.label}</span>
          <span style={{ background: "rgba(255,255,255,0.2)", borderRadius: "12px", padding: "2px 10px", fontSize: "0.78rem" }}>
            {set.segmentScores?.length ?? 0} segments
          </span>
        </div>
        <div style={{ fontSize: "0.78rem", opacity: 0.85, marginTop: "3px" }}>
          {cfg.desc}
          {set.scoringConfig?.formula && (
            <span style={{ marginLeft: "8px", opacity: 0.7 }}>· {set.scoringConfig.formula}</span>
          )}
        </div>
      </div>

      <div style={{ padding: "12px 14px", background: cfg.lightColor }}>
        <div style={{ fontSize: "0.72rem", fontWeight: 700, color: cfg.color, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px" }}>
          Aggregate Metrics
        </div>
        {m.relevance != null && <MetricRow label="Relevance" value={fmtPct(m.relevance)} />}
        <MetricRow label="Reach" value={fmtBig(m.reach)} />
        <MetricRow label="Total Impressions" value={fmtBig(m.totalImpressions)} />
        <MetricRow label="Total Requests" value={fmtBig(m.totalRequests)} />
        <MetricRow label="Viewability" value={fmtPct(m.viewability)} />
        <MetricRow label="Click-Through Rate" value={fmtPct(m.clickThroughRate)} />
        <MetricRow label="Video Completion" value={fmtPct(m.videoCompletionRate)} />
        <MetricRow label="Bid Guidance" value={fmtCpm(m.bidGuidance)} />
        <MetricRow label="Avg CPM" value={fmtCpm(m.avgCpm)} />
        {m.minCpm != null && m.maxCpm != null && (
          <MetricRow label="CPM Range" value={`${fmtCpm(m.minCpm)} – ${fmtCpm(m.maxCpm)}`} />
        )}
        <MetricRow label="Mobile Devices" value={fmtBig(m.mobileDevices)} />
        <MetricRow label="CTV Devices" value={fmtBig(m.connectedTvDevices)} />
      </div>

      <div style={{ flex: 1, padding: "12px 14px", overflow: "auto" }}>
        <button
          onClick={() => setExpanded((e) => !e)}
          style={{
            width: "100%", background: "none", border: "none", cursor: "pointer",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "0 0 8px", fontSize: "0.72rem", fontWeight: 700, color: cfg.color,
            textTransform: "uppercase", letterSpacing: "0.05em",
          }}
        >
          <span>Segments</span>
          <span>{expanded ? "▲ collapse" : "▼ expand"}</span>
        </button>
        {expanded &&
          keywordGroups.map((cat, i) => (
            <KeywordCategory
              key={cat.keyword}
              keyword={cat.keyword}
              scores={cat.scores}
              aggregatedMetrics={cat.aggregatedMetrics}
              segmentMap={segmentMap}
              color={cfg.color}
              defaultExpanded={i === 0}
              hllData={hllMetrics[cat.keyword]}
            />
          ))}
      </div>
    </div>
  );
}

function ResultContent({ result, loading, error, hllMetrics = {} }) {
  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 20px", gap: "12px", color: "#666" }}>
        <div style={{ width: "24px", height: "24px", border: "3px solid #e0e0e0", borderTopColor: "#0076bd", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <span>Building unified audience sets…</span>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ margin: "24px", padding: "16px 20px", background: "#fff3f3", border: "1px solid #ffcdd2", borderRadius: "8px", color: "#c62828", fontSize: "0.9rem" }}>
        <strong>Error:</strong> {error}
      </div>
    );
  }
  if (!result?.success) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 20px", color: "#999", fontSize: "0.95rem" }}>
        Run a search to see unified audience sets.
      </div>
    );
  }

  const { segmentSets = [], segmentMap = {} } = result.data || {};
  const orderedSets = SET_ORDER.map((t) => segmentSets.find((s) => s.type === t)).filter(Boolean);
  segmentSets.forEach((s) => { if (!SET_ORDER.includes(s.type)) orderedSets.push(s); });

  if (orderedSets.length === 0) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 20px", color: "#999", fontSize: "0.95rem" }}>
        No unified sets returned for this search.
      </div>
    );
  }

  const dedupCount = result.data?.segments?.length ?? 0;

  return (
    <div style={{ padding: "16px 20px" }}>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          background: "#fff",
          border: "1px solid #d0e8f5",
          borderRadius: "8px",
          padding: "6px 14px",
          marginBottom: "16px",
          boxShadow: "0 1px 4px rgba(0,118,189,0.08)",
        }}
      >
        <span style={{ fontSize: "1.1rem", fontWeight: 800, color: "#0076bd" }}>{dedupCount}</span>
        <span style={{ fontSize: "0.8rem", color: "#555" }}>flat dedup segments</span>
        <span style={{ fontSize: "0.72rem", color: "#999" }}>(unique across all 3 sets)</span>
      </div>
      <div style={{ display: "flex", gap: "16px", alignItems: "flex-start", flexWrap: "wrap" }}>
        {orderedSets.map((set) => (
          <SetCard key={set.type} set={set} segmentMap={segmentMap} hllMetrics={hllMetrics[set.type] || {}} />
        ))}
      </div>
    </div>
  );
}

function runHllForResult(result, setHll) {
  if (!result?.success) return;
  const { segmentSets = [], segmentMap = {} } = result.data || {};
  const initial = {};
  segmentSets.forEach((set) => {
    const groups = groupByKeyword(set.segmentScores, segmentMap);
    initial[set.type] = Object.fromEntries(groups.map(({ keyword }) => [keyword, { loading: true, metrics: null }]));
  });
  setHll(initial);
  segmentSets.forEach((set) => {
    const groups = groupByKeyword(set.segmentScores, segmentMap);
    groups.forEach(({ keyword, scores }) => {
      graphQLSegmentsApiService
        .enrichEstimate(scores, segmentMap)
        .then((metrics) => setHll((prev) => ({ ...prev, [set.type]: { ...prev[set.type], [keyword]: { loading: false, metrics } } })))
        .catch(() => setHll((prev) => ({ ...prev, [set.type]: { ...prev[set.type], [keyword]: { loading: false, metrics: null } } })));
    });
  });
}

function WeightRow({ label, value, onChange, step = 0.05 }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <span style={{ width: "170px", color: "#555" }}>{label}</span>
      <input
        type="range"
        min={0} max={1} step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{ width: "100px" }}
      />
      <span style={{ width: "32px", textAlign: "right", fontWeight: 600, color: "#333" }}>{value.toFixed(2)}</span>
    </div>
  );
}

function NullableIntRow({ label, value, onChange, placeholder, min = 0 }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <span style={{ width: "170px", color: "#555" }}>{label}</span>
      <input
        type="number"
        min={min}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => {
          const v = parseInt(e.target.value);
          onChange(isNaN(v) ? null : v);
        }}
        style={{ width: "70px", padding: "3px 6px", border: "1px solid #d0d0d0", borderRadius: "4px", fontSize: "0.82rem" }}
      />
    </div>
  );
}

function NullableFloatRow({ label, value, onChange, placeholder, min = 0, step = 0.1 }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <span style={{ width: "170px", color: "#555" }}>{label}</span>
      <input
        type="number"
        min={min}
        step={step}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          onChange(isNaN(v) ? null : v);
        }}
        style={{ width: "70px", padding: "3px 6px", border: "1px solid #d0d0d0", borderRadius: "4px", fontSize: "0.82rem" }}
      />
    </div>
  );
}

export function UnifiedSetsPanel({
  result, loading, error,
  unifiedSetType, setUnifiedSetType,
  topNSegments, setTopNSegments,
  reachGoalM, setReachGoalM,
  onRefresh, hasSearchContext,
  minSegments, setMinSegments,
  andMultiplier, setAndMultiplier,
  highestScaleRelevanceWeight, setHighestScaleRelevanceWeight,
  bestPerformanceRelevanceWeight, setBestPerformanceRelevanceWeight,
  oxsWeight, setOxsWeight,
  llmRelevanceGateEnabled, setLlmRelevanceGateEnabled,
  segmentRetentionRate, setSegmentRetentionRate,
  maxOptimisationRounds, setMaxOptimisationRounds,
  highestScaleStartSize, setHighestScaleStartSize,
  bestPerformanceStartSize, setBestPerformanceStartSize,
  mostRelevantStartSize, setMostRelevantStartSize,
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [hll, setHll] = useState({});

  useEffect(() => { runHllForResult(result, setHll); }, [result]);

  const inputStyle = {
    width: "72px",
    padding: "4px 8px",
    border: "1px solid #d0d0d0",
    borderRadius: "6px",
    fontSize: "0.85rem",
    textAlign: "center",
    outline: "none",
  };

  const typeBtn = (type, label) => ({
    padding: "4px 14px",
    borderRadius: "6px",
    border: unifiedSetType === type ? "1px solid #0076bd" : "1px solid #e0e0e0",
    background: unifiedSetType === type ? "#eff8ff" : "#fafafa",
    color: unifiedSetType === type ? "#0076bd" : "#888",
    fontWeight: unifiedSetType === type ? 700 : 400,
    fontSize: "0.8rem",
    cursor: "pointer",
  });

  return (
    <div style={{ background: "#f5f7fa", minHeight: "200px" }}>
      {/* Config bar */}
      <div
        style={{
          background: "#fff",
          borderBottom: "1px solid #e8e8e8",
          padding: "10px 20px",
          display: "flex",
          alignItems: "center",
          gap: "20px",
          flexWrap: "wrap",
        }}
      >
        {/* Set type selector */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span style={{ fontSize: "0.82rem", color: "#555", fontWeight: 600 }}>Set type:</span>
          <button style={typeBtn("REACH_BASED")} onClick={() => setUnifiedSetType("REACH_BASED")}>Reach Based</button>
          <button style={typeBtn("TOP_N")} onClick={() => setUnifiedSetType("TOP_N")}>Top N</button>
        </div>

        {/* Type-specific config */}
        {unifiedSetType === "TOP_N" && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <label style={{ fontSize: "0.82rem", color: "#555", fontWeight: 600 }}>Top N segments:</label>
            <input
              type="number"
              min={1}
              max={50}
              value={topNSegments}
              onChange={(e) => setTopNSegments(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
              style={inputStyle}
            />
          </div>
        )}
        {unifiedSetType === "REACH_BASED" && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <label style={{ fontSize: "0.82rem", color: "#555", fontWeight: 600 }}>Reach goal (M):</label>
            <input
              type="number"
              min={1}
              value={reachGoalM}
              onChange={(e) => setReachGoalM(Math.max(1, parseInt(e.target.value) || 1))}
              style={{ ...inputStyle, width: "88px" }}
            />
            <span style={{ fontSize: "0.78rem", color: "#999" }}>× 1,000,000</span>
          </div>
        )}

        <button
          onClick={() => setLlmRelevanceGateEnabled(!llmRelevanceGateEnabled)}
          title="When enabled, the backend uses an LLM to verify each segment actually matches the search intent before including it in the unified set."
          style={{
            padding: "5px 12px",
            borderRadius: "6px",
            border: llmRelevanceGateEnabled ? "1px solid #6b4fbb" : "1px solid #e0e0e0",
            background: llmRelevanceGateEnabled ? "#f5f3ff" : "#fafafa",
            color: llmRelevanceGateEnabled ? "#6b4fbb" : "#888",
            fontWeight: llmRelevanceGateEnabled ? 700 : 400,
            fontSize: "0.8rem",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "5px",
          }}
        >
          🧠 AI Gate {llmRelevanceGateEnabled ? "ON" : "OFF"}
        </button>
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          style={{
            padding: "5px 12px",
            borderRadius: "6px",
            border: showAdvanced ? "1px solid #0076bd" : "1px solid #e0e0e0",
            background: showAdvanced ? "#eff8ff" : "#fafafa",
            color: showAdvanced ? "#0076bd" : "#888",
            fontSize: "0.8rem",
            cursor: "pointer",
          }}
        >
          ⚙ Advanced {showAdvanced ? "▲" : "▼"}
        </button>
        <button
          onClick={onRefresh}
          disabled={!hasSearchContext}
          style={{
            padding: "5px 14px",
            background: hasSearchContext ? "#0076bd" : "#ccc",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            cursor: hasSearchContext ? "pointer" : "not-allowed",
            fontSize: "0.82rem",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          {loading ? (
            <span style={{ display: "inline-block", width: "12px", height: "12px", border: `2px solid rgba(255,255,255,0.4)`, borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          ) : "↺"}
          Re-run
        </button>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>

      {showAdvanced && (
        <div style={{
          background: "#fff",
          borderBottom: "1px solid #e8e8e8",
          padding: "14px 20px",
          display: "flex",
          flexWrap: "wrap",
          gap: "20px",
          fontSize: "0.82rem",
        }}>
          <fieldset style={{ border: "1px solid #e8e8e8", borderRadius: "6px", padding: "8px 14px", margin: 0 }}>
            <legend style={{ fontSize: "0.75rem", color: "#666", padding: "0 4px" }}>Scoring weights</legend>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <WeightRow label="Highest Scale relevance" value={highestScaleRelevanceWeight} onChange={setHighestScaleRelevanceWeight} />
              <WeightRow label="Best Performance relevance" value={bestPerformanceRelevanceWeight} onChange={setBestPerformanceRelevanceWeight} />
              <WeightRow label="OXS share" value={oxsWeight} onChange={setOxsWeight} />
            </div>
          </fieldset>

          <fieldset style={{ border: "1px solid #e8e8e8", borderRadius: "6px", padding: "8px 14px", margin: 0 }}>
            <legend style={{ fontSize: "0.75rem", color: "#666", padding: "0 4px" }}>Pool settings</legend>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <NullableIntRow label="Min segments" value={minSegments} onChange={setMinSegments} placeholder="default" min={1} />
              <NullableFloatRow label="AND multiplier" value={andMultiplier} onChange={setAndMultiplier} placeholder="default" min={1} step={0.1} />
              <NullableIntRow label="Highest Scale start size" value={highestScaleStartSize} onChange={setHighestScaleStartSize} placeholder="default" min={1} />
              <NullableIntRow label="Best Performance start size" value={bestPerformanceStartSize} onChange={setBestPerformanceStartSize} placeholder="default" min={1} />
              <NullableIntRow label="Most Relevant start size" value={mostRelevantStartSize} onChange={setMostRelevantStartSize} placeholder="default" min={1} />
            </div>
          </fieldset>

          {unifiedSetType === "REACH_BASED" && (
            <fieldset style={{ border: "1px solid #e8e8e8", borderRadius: "6px", padding: "8px 14px", margin: 0 }}>
              <legend style={{ fontSize: "0.75rem", color: "#666", padding: "0 4px" }}>Reach-based trim</legend>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <WeightRow label="Retention rate" value={segmentRetentionRate ?? 0.3} onChange={(v) => setSegmentRetentionRate(v)} step={0.05} />
                <NullableIntRow label="Max rounds" value={maxOptimisationRounds} onChange={setMaxOptimisationRounds} placeholder="15" min={1} />
              </div>
            </fieldset>
          )}
        </div>
      )}

      {/* Result */}
      <ResultContent result={result} loading={loading} error={error} hllMetrics={hll} />
    </div>
  );
}

export default UnifiedSetsPanel;
